/*
 * Author: Chao.Wang
 * File Created: Tuesday, 25-01-07 07:44 pm
 * Last Modified: Friday, 25-03-07 04:58 pm, by Ben Zhao
 */

import { persistCache } from 'apollo3-cache-persist';
import fetch from 'cross-fetch';
import { GraphQLFormattedError } from 'graphql';

import {
  ApolloClient,
  ApolloQueryResult,
  createHttpLink,
  gql,
  InMemoryCache,
  MaybeMasked,
  NormalizedCacheObject,
  Observable,
  QueryOptions,
  useQuery,
} from '@apollo/client';
import { NetworkError } from '@apollo/client/errors';
import { onError } from '@apollo/client/link/error';
import { Logger } from '@web/utils';

import { GraphQLQueryOptions } from './graphql';
import { filterDisabled } from './utils';

interface ApolloClientOptions {
  onError?: (error: {
    graphQLErrors?: ReadonlyArray<GraphQLFormattedError>;
    networkError?: NetworkError;
  }) => void;
}

const logger = new Logger({
  module: 'graphql',
});

// 创建错误处理链接
const getErrorLink = (params?: Pick<ApolloClientOptions, 'onError'>) => {
  const { onError: onGraphqlError } = params || {};

  return onError(({ graphQLErrors, networkError, operation, forward }) => {
    try {
      const client = getApolloClient();
      const cachedData = client.readQuery({
        query: operation.query,
        variables: operation.variables,
      });

      if (cachedData) {
        logger.log('Using cached data:', cachedData);
        return new Observable((observer) => {
          observer.next({ data: cachedData });
          observer.complete();
        });
      } else {
        logger.log('No cached data available.');

        onGraphqlError?.({
          graphQLErrors,
          networkError,
        });
      }
    } catch (cacheError) {
      logger.error('Error reading from cache:', cacheError);
    }

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message }) => {
        logger.error('graphQLErrors: ', message);
      });
    }
    // 处理网络错误
    if (networkError) {
      logger.error('networkError: ', networkError.message);
    }
    return forward(operation);
  });
};

// 创建 HTTP 链接
const httpLink = createHttpLink({
  uri: process.env.NEXT_PUBLIC_GRAPHQL_PATH,
  fetch,
});

// A place to store the client instance on the client-side
let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;

const createApolloClient = (params?: ApolloClientOptions) => {
  const isServer = typeof window === 'undefined';

  // Create a new cache instance for every request on SSR
  const cache = new InMemoryCache();

  // Only on the client side, persist the cache to localStorage
  if (!isServer) {
    persistCache({
      cache,
      storage: window.localStorage, // Store Apollo cache in localStorage
    }).then(() => {
      console.log('Apollo Cache persisted to localStorage');
    });
  }

  // Create a new Apollo Client instance for every SSR request
  return new ApolloClient({
    cache,
    link: getErrorLink({ onError: params?.onError }).concat(httpLink), // Replace with your links
    ssrMode: isServer, // Disable cache for SSR (to fetch fresh data)
    defaultOptions: {
      query: {
        fetchPolicy: 'cache-first', // Use cache for client-side queries
      },
    },
    devtools: {
      enabled: process.env.NODE_ENV === 'development',
    },
  });
};

// This function ensures we always create a new Apollo Client for SSR
const getApolloClient = () => {
  if (typeof window === 'undefined') {
    // Server-side: always create a new Apollo Client instance for each request
    return createApolloClient();
  }

  // Client-side: use the cached instance if it exists
  if (!apolloClient) {
    apolloClient = createApolloClient();
  }

  return apolloClient;
};

// 基础请求
const query = async <T = unknown>(
  queryName: QueryOptions['query'],
  options?: GraphQLQueryOptions<T>
): Promise<ApolloQueryResult<MaybeMasked<T>>> => {
  const { variables, force, noCache, ...restOptions } = options || {};
  try {
    const client = getApolloClient();

    const fetchPolicy = noCache
      ? 'no-cache'
      : force
        ? 'network-only'
        : 'cache-first';

    const response = await client.query<T>({
      query: queryName,
      fetchPolicy,
      ...restOptions,
      variables: {
        status: process.env.NEXT_PUBLIC_GRAPHQL_STATUS,
        ...variables,
      },
    });
    // Check if the data contains a "disabled" field and filter accordingly
    const data = response.data;

    // Assuming the data is an object and could have nested arrays
    if (data && typeof data === 'object') {
      // Apply the filter to all values in the data object
      const filteredData = filterDisabled(data);
      return {
        ...response,
        data: filteredData, // Return the filtered data
      };
    }

    // If the data is not an array, return as is
    return response;
  } catch (error: any) {
    return Promise.reject({
      message: `GraphQL Query Failed: ${error.message}`,
      statusCode: error?.networkError?.statusCode || 500,
      graphQLErrors: error?.graphQLErrors,
      stack: error.stack,
      ignoreSentry: true,
    });
  }
};

// 客户端请求
export const useGraphqlQuery = <T>(
  queryName: QueryOptions['query'],
  options: GraphQLQueryOptions<T>
) => {
  const { variables, force, noCache, ...restOptions } = options || {};
  const mergedVariables: any = {
    status: process.env.NEXT_PUBLIC_GRAPHQL_STATUS,
    ...variables,
  };
  const fetchPolicy = noCache
    ? 'no-cache'
    : force
      ? 'network-only'
      : 'cache-first';

  return useQuery(queryName, {
    variables: mergedVariables,
    ...restOptions,
    fetchPolicy,
  });
};

/** web 客户端可以重置 ApolloClient */
export const initApolloClient = (params?: ApolloClientOptions) => {
  if (typeof window === 'undefined') return;

  apolloClient = createApolloClient(params);
};

export { ApolloProvider } from '@apollo/client';

export const graphql = {
  query,
  gql,
  getApolloClient,
};
